// This module keeps track of env-variables that may be used in the application.

// Once we've refactored the app so that we can keep the .env-file checked into
// source control, it will be less useful.

import { isStoredFeatureEnabled } from "src/components/Flags/flagContextUtil"
import { FeatureFlag } from "src/constants/featureFlags"

export const REACT_APP_ALWAYS_SHOW_FLAGS = import.meta.env
  .REACT_APP_ALWAYS_SHOW_FLAGS
  ? true
  : undefined

/** Resolves OAuth config — the paradise (admin) client when `adminClient` is
 *  true, otherwise the regular app client. */
export function getOAuthClientCredentials({
  adminClient,
}: {
  adminClient: boolean
}) {
  const credentials = adminClient
    ? {
        clientId: import.meta.env.REACT_APP_PARADISE_CLIENT_ID,
        clientSecret: import.meta.env.REACT_APP_PARADISE_CLIENT_SECRET,
      }
    : {
        clientId: import.meta.env.REACT_APP_CLIENT_ID,
        clientSecret: import.meta.env.REACT_APP_CLIENT_SECRET,
      }

  return {
    ...credentials,
    // Do NOT change this route unless also changing the allowed redirects for the client in Paradise
    clientRedirectPath: "/auth/redirect",
    backendUri: import.meta.env.REACT_APP_API_URL,
  }
}

export const isDev = process.env.NODE_ENV === "development"
const isProd = process.env.NODE_ENV === "production"

export const isProdEnv =
  window.location.hostname.includes("web.minut.com") && isProd

export const isStagingEnv =
  window.location.hostname.includes("web.staging.minut.com") && isProd

/** @knipIgnore */
export const isDevelopEnv =
  window.location.hostname.includes("web.develop.staging.minut.com") && isProd

export const isDemoEnv =
  window.location.hostname.includes("web.demo.minut.com") && isProd

// Paradise environment detection (computed early for use in client config)
export const isParadiseStagingEnv =
  window.location.hostname.includes("paradise2.staging.minut.com") ||
  window.location.hostname.includes("paradise.staging.minut.com")

export const isParadiseProdEnv =
  window.location.hostname.includes("paradise2.minut.com") ||
  window.location.hostname.includes("paradise.minut.com")

// Function since we want to compute this at runtime, not at module load time, since the window.location may be recomputed
export function isParadiseDevEnv() {
  return window.location.pathname.startsWith("/paradise")
}

export function isParadiseEnv(): boolean {
  return isParadiseProdEnv || isParadiseStagingEnv || isParadiseDevEnv()
}

/** True when the sealAuth feature flag is on. Gates the seal auth session
 *  flows, e.g. silent re-auth through the authorize endpoint with
 *  prompt=none. */
export function isSealAuthEnabled(): boolean {
  return isStoredFeatureEnabled(FeatureFlag.SEAL_AUTH)
}

/** True only in a paradise env AND with the sealAuth flag on. Gates the paradise
 *  (admin) OAuth client and the paradise token storage bucket: without sealAuth,
 *  even a paradise-env user uses the regular app client and the regular storage
 *  bucket, since the backend-driven auth pages don't support admin clients. */
export function isSealAdminAuthContext(): boolean {
  return isParadiseEnv() && isSealAuthEnabled()
}
